"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const core_1 = require("@emotion/core");
const lodash_1 = require("lodash");
const common_utils_1 = require("@zapier/common-utils");
const design_system_1 = require("@zapier/design-system");
const FloatingMenu_1 = __importDefault(require("../../FloatingMenu"));
const common_components_1 = require("@zapier/common-components");
const OptionLabel = ({ item }) => (common_utils_1.isolateComponent(design_system_1.Text, { fontWeight: 600, tag: "p", type: "altText900" },
    item.isStale ? 'EXPIRED - ' : '',
    item.label));
const OptionValue = ({ item }) => (common_utils_1.isolateComponent(design_system_1.Text, { color: "grey500", tag: "p", type: "altText800" },
    "(used in ",
    item.numZaps,
    " ",
    common_utils_1.pluralize('Zap', item.numZaps),
    ")"));
const dropdownMenuItemContainer = (highlighted) => core_1.css `
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${highlighted ? design_system_1.Colors.blue100 : design_system_1.Colors.transparent};
  border-radius: 10px;
`;
const dropdownAuthTypeContainer = core_1.css `
  margin-left: auto;
  display: flex;
  align-items: center;
`;
const authTypeIcons = {
    private: 'actionLock',
    shared: 'personGroup',
    restricted: 'miscHand',
};
function DropdownMenuItem({ item, getItemProps, checked, highlighted, groupId, }) {
    const itemProps = getItemProps({ item });
    const isSelected = Boolean(checked || highlighted);
    return (common_utils_1.isolateComponent("div", Object.assign({ css: dropdownMenuItemContainer(highlighted) }, itemProps, { "aria-selected": isSelected, role: "option" }),
        common_utils_1.isolateComponent(design_system_1.FormLabel, { layout: "auto-content" },
            common_utils_1.isolateComponent(design_system_1.Radio, { checked: isSelected, name: groupId, onChange: lodash_1.noop, tabIndex: -1, value: '' + item.value }),
            common_utils_1.isolateComponent("div", null,
                common_utils_1.isolateComponent(OptionLabel, { item: item }),
                common_utils_1.isolateComponent(OptionValue, { item: item }))),
        common_utils_1.isolateComponent("div", { css: dropdownAuthTypeContainer },
            common_utils_1.isolateComponent(design_system_1.Icon, { color: "grey500", name: authTypeIcons[item.authType], size: 16 }),
            common_utils_1.isolateComponent(design_system_1.Spacer, { width: 4 }),
            common_utils_1.isolateComponent(design_system_1.Text, { color: "grey700", type: "altText900" }, lodash_1.capitalize(item.authType)))));
}
const floatingMenuContentContainer = core_1.css `
  margin: 15px;
`;
const headerContainer = core_1.css `
  border-bottom: 1px solid ${design_system_1.Colors.grey200};
  padding: 10px 15px;
`;
function DropdownMenu(props) {
    const { items, getMenuProps, getItemProps, highlightedIndex, setHighlightedIndex, selectedItem, selectItem, closeMenu, onConnect, } = props;
    const menuProps = getMenuProps({ 'aria-label': 'Choose an account' });
    const groupId = react_1.useMemo(() => '' + Math.random(), []);
    const [query, setQuery] = react_1.useState('');
    const onChangeQuery = (e) => setQuery(e.target.value);
    const visibleItems = lodash_1.filter(items, item => lodash_1.isEmpty(query) || lodash_1.toLower(item.label).includes(lodash_1.toLower(query)));
    const visibleItemsCount = lodash_1.size(visibleItems);
    const constrain = (newIndex) => newIndex < 0
        ? visibleItemsCount - 1
        : newIndex >= visibleItemsCount
            ? 0
            : newIndex;
    const onUpArrow = common_utils_1.createKeyHandler('up', () => {
        const newIndex = lodash_1.isNumber(highlightedIndex) ? highlightedIndex - 1 : 0;
        setHighlightedIndex(constrain(newIndex));
    });
    const onDownArrow = common_utils_1.createKeyHandler('down', () => {
        const newIndex = lodash_1.isNumber(highlightedIndex) ? highlightedIndex + 1 : 0;
        setHighlightedIndex(constrain(newIndex));
    });
    const onSelect = common_utils_1.createKeyHandler('enter', () => {
        selectItem(visibleItems[highlightedIndex || 0]);
    });
    const onQueryKeyDown = (e) => {
        onUpArrow(e);
        onDownArrow(e);
        onSelect(e);
    };
    const searchInputRef = react_1.useRef(null);
    react_1.useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);
    const maybeConnectButton = onConnect ? (common_utils_1.isolateComponent(react_1.Fragment, null,
        common_utils_1.isolateComponent(design_system_1.Spacer, { height: 15 }),
        common_utils_1.isolateComponent(design_system_1.Button, { ariaLabel: "Connect a new account", color: "greyAlt", isFullWidth: true, onClick: onConnect, size: "small" }, "+ Connect a new account"))) : null;
    return (common_utils_1.isolateComponent(FloatingMenu_1.default, { onClickOutside: closeMenu },
        common_utils_1.isolateComponent(common_components_1.ClickPropagationBarrier, null,
            common_utils_1.isolateComponent("div", Object.assign({ "aria-label": "Choose an account" }, menuProps),
                common_utils_1.isolateComponent("div", { css: headerContainer },
                    common_utils_1.isolateComponent(design_system_1.Text, { type: "subtitle700" }, "Choose\u2026")),
                common_utils_1.isolateComponent("div", { css: floatingMenuContentContainer },
                    common_utils_1.isolateComponent(design_system_1.TextInput, { ariaLabel: "Search available accounts", onChange: onChangeQuery, onKeyDown: onQueryKeyDown, placeholder: "Search", ref: searchInputRef, renderIconBefore: ({ iconSize }) => {
                            return common_utils_1.isolateComponent(design_system_1.Icon, { name: "magnifyingGlass", size: iconSize });
                        }, value: query }),
                    common_utils_1.isolateComponent(design_system_1.Spacer, { height: 15 }),
                    lodash_1.map(visibleItems, (item, index) => (common_utils_1.isolateComponent(DropdownMenuItem, { checked: Boolean(selectedItem && selectedItem.value === item.value), getItemProps: getItemProps, groupId: groupId, highlighted: highlightedIndex === index, item: item, key: item.value }))),
                    maybeConnectButton)))));
}
exports.default = DropdownMenu;
